import { useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Alert } from "antd";
import dayjsTZ from "../../../utils/dayjs";
import AgGridComponent from "../../AG-grid/AgGridComponent";
import PunonjesitContext from "../store/PunonjesitContext";
import SpecsPeriodFilter from "../components/SpecsPeriodFilter";
import { colDefs } from "./colDefs";
import { getShiftHoursBreakdown } from "./shiftHours";
import ClockInDetailModal from "./ClockInDetailModal";

const CLOCKIN_TYPES_FIELD_ID = "e685cd30-b119-11ec-829e-112b583587c7";

const ClockIns = () => {
  const { employee, specsData } = useContext(PunonjesitContext);
  const { programFields } = useSelector((state) => state.programFields);

  const [gridApi, setGridApi] = useState(null);
  const [selectedShift, setSelectedShift] = useState(null);

  const dayTypeOptions = useMemo(() => {
    if (!programFields) return [];
    const field = programFields.find(
      (el) => el?.fieldId === CLOCKIN_TYPES_FIELD_ID
    );
    return field?.fieldOptions || [];
  }, [programFields]);

  const dayTypes = useMemo(() => {
    const toReturn = {};
    dayTypeOptions.forEach((el = {}) => {
      if (el?.name) toReturn[el.name] = el.description;
    });
    return toReturn;
  }, [dayTypeOptions]);

  const columnDefs = useMemo(() => colDefs({ dayTypes }), [dayTypes]);

  const rowData = useMemo(() => {
    const data = specsData?.clockings || [];
    const breakTime = employee?.breakTime || [];
    const isBreakTimePaid = !!employee?.isBreakTimePaid;

    return data
      .map((el) => {
        const hours = getShiftHoursBreakdown({
          clockInDate: el?.clockInDate,
          clockOutDate: el?.clockOutDate,
          breakTime,
          isBreakTimePaid,
        });

        return {
          ...el,
          fillim_turni: el?.clockInDate,
          mbarim_turni: el?.clockOutDate,
          weekDay: el?.clockInDate
            ? dayjsTZ(el.clockInDate).locale("sq").format("dddd")
            : "",
          dayTypeLabel: dayTypes[el?.dayType] || el?.dayType || "",
          duration: hours.grossHours,
          breakHours: hours.breakHours,
          workingHours: hours.workingHours,
          paidHours: hours.paidHours,
          breakPaidLabel: hours.breakApplied
            ? isBreakTimePaid
              ? "I paguar"
              : "I papaguar"
            : "—",
          breakTimeInterval:
            Array.isArray(breakTime) && breakTime.length >= 2
              ? `${breakTime[0]} - ${breakTime[1]}`
              : "",
        };
      })
      .sort((a, b) => (a?.clockInDate || 0) - (b?.clockInDate || 0));
  }, [
    specsData?.clockings,
    dayTypes,
    employee?.breakTime,
    employee?.isBreakTimePaid,
  ]);

  useEffect(() => {
    if (gridApi) {
      gridApi.setGridOption?.("loading", !!specsData?.loading);
    }
  }, [gridApi, specsData?.loading]);

  const EXPORT_COLS = [
    { en: "clockInDate", al: "Data e turnit", format: "DD/MM/YYYY" },
    { en: "weekDay", al: "Dita" },
    { en: "dayTypeLabel", al: "Lloji i turnit" },
    {
      en: "approved",
      al: "Statusi",
      replace: {
        true: "I aprovuar",
        false: "I pa aprovuar",
      },
    },
    { en: "duration", al: "Kohëzgjatja (orë)" },
    { en: "breakTimeInterval", al: "Orari i pushimit" },
    { en: "breakHours", al: "Pushimi (orë)" },
    { en: "breakPaidLabel", al: "Pushim i paguar" },
    { en: "workingHours", al: "Orët e punës" },
    { en: "paidHours", al: "Orët e paguara" },
  ];

  const ITEMS = {
    search: { handleClean: () => gridApi?.setFilterModel?.(null) },
    icons: {
      filter: {
        filterKeys: [
          {
            key: "dayTypeLabel",
            label: "Lloji i turnit",
            type: "string",
            filterType: "equals",
          },
          {
            key: "breakPaidLabel",
            label: "Pushim i paguar",
            type: "string",
            filterType: "equals",
          },
        ],
      },
      excel: { tableCols: EXPORT_COLS },
      pdf: { tableCols: EXPORT_COLS },
      print: { tableCols: EXPORT_COLS },
    },
  };

  return (
    <div className="globalPageWrapper">
      <SpecsPeriodFilter variant="full" />
      {!specsData?.loading && rowData.length === 0 ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={`Nuk ka të dhëna për ${specsData?.periodLabel || "këtë periudhë"}`}
        />
      ) : null}
      <AgGridComponent
        className="clockins-clickable"
        gridApi={gridApi}
        rowData={rowData}
        rowSelection={{ mode: "multiRow" }}
        onGridReady={(p) => setGridApi(p.api)}
        columnDefs={columnDefs}
        paginationPageSize={23}
        idKey={"clockingId"}
        rowClickKey={"clockingId"}
        onRowClick={(_, row) => setSelectedShift(row)}
        headerProps={{
          items: ITEMS,
          exportTitle: `Oraret - ${employee?.employeeFirstName} ${employee?.employeeLastName}`,
        }}
        counterProps={{
          className: "rowFlex",
          title: "Totali i orëve të paguara (Aprovuar)",
          data: rowData?.map((el) => el?.paidHours || 0),
          derivedData: rowData?.map((el) =>
            !!el?.approved ? el?.paidHours || 0 : 0
          ),
        }}
      />
      <ClockInDetailModal
        open={!!selectedShift}
        onClose={() => setSelectedShift(null)}
        shift={selectedShift}
        leaves={specsData?.leaves || []}
        clockings={specsData?.clockings || []}
        dayTypes={dayTypes}
        dayTypeOptions={dayTypeOptions}
      />
    </div>
  );
};

export default ClockIns;
